import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ChatCircleDots, X, PaperPlaneRight, Circle, CheckCircle } from "@phosphor-icons/react";
import { portalApi } from "@/lib/portalApi";
import { BRAND } from "./portalBrand";

/**
 * Customer support chat — a floating bubble on the portal. Polls the shared support
 * queue; any online agent in the ERP can pick it up and reply.
 */
export default function PortalChat() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [online, setOnline] = useState(false);
  const [unread, setUnread] = useState(0);
  const [sending, setSending] = useState(false);
  const [closed, setClosed] = useState(false);
  const scroller = useRef(null);
  const lastCount = useRef(0);
  const prevStatus = useRef(null);

  const poll = async () => {
    try {
      const { data } = await portalApi.get("/portal/chat");
      setOnline(!!data.staff_online);
      const status = data.conversation?.status || null;
      // Notify the customer when staff close the chat.
      if (prevStatus.current && prevStatus.current !== "closed" && status === "closed") {
        toast("Your chat was closed by our team. Send a message to start a new one.");
        if (!open) setUnread((u) => u + 1);
      }
      prevStatus.current = status;
      setClosed(status === "closed");
      const msgs = data.messages || [];
      setMessages(msgs);
      // Unread = new staff messages arrived while panel closed.
      if (!open) {
        const staffCount = msgs.filter((m) => m.sender_type === "staff").length;
        if (staffCount > lastCount.current && lastCount.current !== 0) setUnread((u) => u + (staffCount - lastCount.current));
        lastCount.current = staffCount;
      } else {
        lastCount.current = msgs.filter((m) => m.sender_type === "staff").length;
      }
    } catch { /* ignore transient */ }
  };

  useEffect(() => {
    poll();
    const id = setInterval(poll, open ? 4000 : 12000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => { if (open) setUnread(0); }, [open]);
  useEffect(() => { if (scroller.current) scroller.current.scrollTop = scroller.current.scrollHeight; }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setSending(true);
    const optimistic = { id: `tmp-${Date.now()}`, sender_type: "customer", text, created_at: new Date().toISOString() };
    if (closed) {
      // Starting a fresh chat after the previous one was closed.
      setMessages([optimistic]);
      setClosed(false);
      prevStatus.current = null;
    } else {
      setMessages((m) => [...m, optimistic]);
    }
    setInput("");
    try {
      await portalApi.post("/portal/chat/message", { text });
      poll();
    } catch { /* keep optimistic message */ } finally { setSending(false); }
  };

  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} data-testid="portal-chat-launcher"
          className="fixed bottom-5 right-5 z-50 h-14 w-14 rounded-full bg-blue-700 hover:bg-blue-800 text-white shadow-lg shadow-blue-900/30 flex items-center justify-center transition-colors">
          <ChatCircleDots size={26} weight="fill" />
          {unread > 0 && <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-orange-500 text-white text-[11px] font-semibold flex items-center justify-center border-2 border-slate-50">{unread > 9 ? "9+" : unread}</span>}
        </button>
      )}

      {open && (
        <div className="fixed bottom-5 right-5 z-50 w-[360px] max-w-[calc(100vw-2rem)] h-[540px] max-h-[calc(100vh-2rem)] bg-white border border-slate-200 rounded-xl shadow-2xl flex flex-col overflow-hidden" data-testid="portal-chat-panel">
          <div className="px-4 py-3 text-white flex items-center justify-between" style={{ background: "linear-gradient(120deg,#1d4ed8,#2563eb)" }}>
            <div>
              <div className="font-semibold text-sm">{BRAND.name} Support</div>
              <div className="text-[11px] inline-flex items-center gap-1.5 text-blue-100">
                <Circle size={8} weight="fill" className={online ? "text-emerald-300" : "text-slate-300"} />
                {online ? "We're online — usually reply in a few minutes" : "Offline — leave a message, we'll get back to you"}
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="text-blue-100 hover:text-white"><X size={18} /></button>
          </div>

          <div ref={scroller} className="flex-1 overflow-y-auto px-3 py-3 space-y-2 bg-slate-50">
            {messages.length === 0 && <div className="text-center text-slate-400 text-sm mt-8">Hi! How can we help with your shipments today?</div>}
            {messages.map((m) => (
              <div key={m.id} className={`flex ${m.sender_type === "customer" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${m.sender_type === "customer" ? "bg-blue-700 text-white rounded-br-sm" : m.sender_type === "system" ? "bg-slate-50 border border-slate-200 text-slate-600 rounded-bl-sm italic" : "bg-white border border-slate-200 text-slate-800 rounded-bl-sm"}`}>
                  {m.sender_type !== "customer" && m.sender_name && <div className="text-[10px] font-semibold text-blue-700 mb-0.5 not-italic">{m.sender_type === "system" ? "Support · auto-reply" : m.sender_name}</div>}
                  <div className="whitespace-pre-wrap">{m.text}</div>
                </div>
              </div>
            ))}
          </div>

          {closed && (
            <div className="px-3 py-2 bg-slate-100 border-t border-slate-200 text-[12px] text-slate-500 text-center inline-flex items-center justify-center gap-1.5">
              <CheckCircle size={13} className="text-emerald-500" /> This chat was closed. Send a message to start a new one.
            </div>
          )}
          <div className="border-t border-slate-200 p-2.5 flex items-end gap-2 bg-white">
            <textarea value={input} onChange={(e) => setInput(e.target.value)} rows={1}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Type your message…" data-testid="portal-chat-input"
              className="flex-1 resize-none max-h-24 px-3 py-2 bg-slate-50 border border-slate-300 rounded-md text-sm text-slate-900 focus:outline-none focus:border-blue-500" />
            <button onClick={send} disabled={sending || !input.trim()} data-testid="portal-chat-send"
              className="h-9 w-9 shrink-0 rounded-md bg-blue-700 hover:bg-blue-800 text-white flex items-center justify-center disabled:opacity-40">
              <PaperPlaneRight size={16} weight="fill" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
